import { useEffect, useState } from 'react'
import { Compass, Plus, Trash2, AlertCircle } from 'lucide-react'
import { api } from '@/lib/api'

interface RoutingRule {
  id: string
  name: string
  conditionsJson: Record<string, unknown>
  action: 'ASSIGN_USER' | 'ASSIGN_BOT' | 'ESCALATE' | 'TAG' | 'SNOOZE' | 'CLOSE'
  actionPayloadJson: Record<string, unknown>
  slaMinutes: number | null
  priority: number
  active: boolean
  updatedAt: string
}

const ACTIONS = ['ASSIGN_BOT', 'ASSIGN_USER', 'ESCALATE', 'TAG', 'SNOOZE', 'CLOSE'] as const

const EXAMPLE_CONDITIONS = JSON.stringify(
  { channel: 'WHATSAPP', timeOfDayOutside: ['09:00', '19:00'], dayOfWeekIn: [1, 2, 3, 4, 5] },
  null,
  2,
)

const EXAMPLE_PAYLOAD = JSON.stringify({ botProfileId: 'bot-default' }, null, 2)

export function RoutingRulesPage() {
  const [items, setItems] = useState<RoutingRule[]>([])
  const [draftName, setDraftName] = useState('')
  const [draftAction, setDraftAction] = useState<(typeof ACTIONS)[number]>('ASSIGN_BOT')
  const [draftConditions, setDraftConditions] = useState(EXAMPLE_CONDITIONS)
  const [draftPayload, setDraftPayload] = useState(EXAMPLE_PAYLOAD)
  const [draftSla, setDraftSla] = useState('15')
  const [draftPriority, setDraftPriority] = useState('100')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function load() {
    const r = await api<{ items: RoutingRule[] }>('/v1/marketing/routing-rules')
    setItems(r.items)
  }

  useEffect(() => {
    void load()
  }, [])

  async function create(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      await api('/v1/marketing/routing-rules', {
        method: 'POST',
        body: JSON.stringify({
          name: draftName,
          action: draftAction,
          conditionsJson: JSON.parse(draftConditions),
          actionPayloadJson: JSON.parse(draftPayload),
          slaMinutes: draftSla ? Number(draftSla) : undefined,
          priority: Number(draftPriority),
        }),
      })
      setDraftName('')
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function remove(r: RoutingRule) {
    if (!confirm(`¿Eliminar la regla "${r.name}"?`)) return
    await api(`/v1/marketing/routing-rules/${r.id}`, { method: 'DELETE' })
    await load()
  }

  return (
    <div className="p-6 max-w-5xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <Compass className="size-5 text-blueprint-700" />
          Routing Rules
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          gobernanza conversaciones · bot↔humano por canal/horario/tag · evaluadas por priority asc
        </p>
        <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-mono bg-amber-50 border border-amber-200 text-amber-900">
          <AlertCircle className="size-3" />
          schema listo · engine Sprint 4
        </div>
      </header>

      <form onSubmit={(e) => void create(e)} className="bp-card p-4 mb-4 space-y-3">
        <div className="grid grid-cols-12 gap-2">
          <input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="nombre"
            required
            className="col-span-5 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
          />
          <select
            value={draftAction}
            onChange={(e) => setDraftAction(e.target.value as (typeof ACTIONS)[number])}
            className="col-span-3 border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
          >
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <input
            value={draftPriority}
            onChange={(e) => setDraftPriority(e.target.value)}
            placeholder="prioridad"
            type="number"
            className="col-span-2 border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
          />
          <input
            value={draftSla}
            onChange={(e) => setDraftSla(e.target.value)}
            placeholder="SLA min"
            type="number"
            className="col-span-2 border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              condiciones (JSON)
            </span>
            <textarea
              value={draftConditions}
              onChange={(e) => setDraftConditions(e.target.value)}
              spellCheck={false}
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-xs font-mono min-h-[120px]"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              payload de acción (JSON)
            </span>
            <textarea
              value={draftPayload}
              onChange={(e) => setDraftPayload(e.target.value)}
              spellCheck={false}
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-xs font-mono min-h-[120px]"
            />
          </label>
        </div>
        {err && (
          <div className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            {err}
          </div>
        )}
        <button
          type="submit"
          className="bp-btn-primary"
          disabled={busy || !draftName.trim()}
        >
          <Plus className="size-3" /> Crear regla
        </button>
      </form>

      <div className="bp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
            <tr>
              <th className="px-3 py-2 text-left">Prioridad</th>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Acción</th>
              <th className="px-3 py-2 text-left">Condiciones</th>
              <th className="px-3 py-2 text-right">SLA</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-ink-900/10">
                <td className="px-3 py-2 font-mono">{r.priority}</td>
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  <span className="bp-pill">{r.action}</span>
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-ink-700">
                  {Object.entries(r.conditionsJson)
                    .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
                    .join(' · ')}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {r.slaMinutes ? `${r.slaMinutes}m` : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => void remove(r)}
                    className="bp-btn text-red-700"
                    title="Eliminar"
                  >
                    <Trash2 className="size-3" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-xs text-ink-700/50">
                  Sin reglas configuradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
